import { useTranslator } from '@bluebeam/react-i18n';
import Plus from 'components/Icons/Plus';
import SearchInput from 'components/inputs/SearchInput';
import OutsideClick from 'components/OutsideClick';
import SelectedOptionLabel from 'components/SelectedOptionLabel';
import Tabs from 'components/Tabs';
import Tab from 'components/Tabs/Tab';
import { List, OrderedSet } from 'immutable';
import React, {
  FC,
  MouseEvent,
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ProjectGroup } from 'records/ProjectGroup';
import AssigneesSearchResult from './AssigneesSearchResults';
import './AssigneesStyles.css';
import AssigneesTab from './AssigneesTab';
import GroupAssigneesTab from './GroupAssigneesTab';
import TabsPopover from './TabsPopover';

interface GetInitialAssigneesMethod {
  (): OrderedSet<string>;
}
interface Props {
  id: string;
  assigneesOptions: OrderedSet<string>;
  groupAssigneesOptions: List<ProjectGroup>;
  editable: boolean;
  onChange: (values: OrderedSet<string>, type: AssigneesType) => void;
  getInitialSelectedAssignees: GetInitialAssigneesMethod;
  getInitialSelectedGroups: GetInitialAssigneesMethod;
}

type AssigneesType = 'assignees' | 'groupAssignees';
type TabType = 'Individuals' | 'Groups';

const AssigneesTabsMultiSelect: FC<Props> = (props: Props) => {
  const { translate } = useTranslator(['mapView']);
  const {
    assigneesOptions,
    groupAssigneesOptions,
    editable,
    onChange,
    getInitialSelectedAssignees,
    getInitialSelectedGroups,
  } = props;
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<TabType>('Individuals');
  const [isAssignneeAdded, setIsAssigneeAdded] = useState<boolean>(false);
  const [isAssigneeRemoved, setIsAssigneeRemoved] = useState<boolean>(false);
  const [selectedAssignees, setSelectedAssignees] = useState(
    getInitialSelectedAssignees()
  );
  const [selectedGroups, setSelectedGroups] = useState(
    getInitialSelectedGroups()
  );
  const [assigneesToDisplay, setAssigneesToDisplay] = useState(List());
  const assigneeInputRef = useRef<HTMLInputElement>(null);
  const [searchIndividuals, setSearchIndividuals] = useState(
    OrderedSet<string>(assigneesOptions)
  );
  const [searchGroups, setSearchGroups] = useState(
    List<ProjectGroup>(groupAssigneesOptions)
  );
  const [openSearch, setOpenSearch] = useState<boolean>(false);
  const [hideSearchIcon, setHideSearchIcon] = useState<boolean>(false);

  useEffect(() => {
    const assignees = mergeIndividualAndGroupAssignees(
      selectedAssignees,
      selectedGroups
    );
    setAssigneesToDisplay(assignees);
  }, [assigneesOptions, groupAssigneesOptions]);

  useEffect(() => {
    scrollToRecentlyAddedAssignee();
    return () => resetAssigneeScrollFlags();
  }, [selectedAssignees, selectedGroups]);

  const resetAssigneeScrollFlags = (): void => {
    setIsAssigneeRemoved(false);
    setIsAssigneeAdded(false);
  };

  const scrollToRecentlyAddedAssignee = (): void => {
    if (isAssignneeAdded && !isAssigneeRemoved) {
      scrollToRecentlyAdded(assigneeInputRef);
    }
  };

  const handleTabChange = (selectedTab: string): void => {
    setSelectedTab(selectedTab as TabType);
  };

  const scrollToRecentlyAdded = (ref: RefObject<HTMLInputElement>): void => {
    ref?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const updateAssignees = (selectedAssigneesChanged: OrderedSet<string>) => {
    setSelectedAssignees(selectedAssigneesChanged);
    onChange(selectedAssigneesChanged, 'assignees');
  };

  const updateGroupAssignees = (changedValue: OrderedSet<string>) => {
    setSelectedGroups(changedValue);
    onChange(changedValue, 'groupAssignees');
  };

  const handleRemoveSelectedGroup = (
    event: MouseEvent | null,
    value: string
  ): void => {
    if (event) {
      event.stopPropagation();
    }
    setIsAssigneeRemoved(true);
    const updatedGroups = selectedGroups.filter((group) => group !== value);
    updateGroupAssignees(updatedGroups);

    setAssigneesToDisplay((previousAssignees) => {
      return previousAssignees.filter((assignee) => assignee.key !== value);
    });
  };

  const handleRemoveSelectedAssignee = (
    event: MouseEvent | null,
    value: string
  ): void => {
    if (event) {
      event.stopPropagation();
    }
    setIsAssigneeRemoved(true);

    const selectedAssigneesChanged = selectedAssignees.filter(
      (assignee) => assignee !== value
    );
    updateAssignees(selectedAssigneesChanged);

    setAssigneesToDisplay((previousAssignees) => {
      return previousAssignees.filter((assignee) => assignee.key !== value);
    });
  };

  const handleRemoveAll = (event: MouseEvent | null): void => {
    if (event) {
      event.stopPropagation();
    }
    setIsAssigneeRemoved(true);

    const selectedAssigneesChanged = OrderedSet([]);
    updateAssignees(selectedAssigneesChanged);
    updateGroupAssignees(OrderedSet([]));
    setAssigneesToDisplay(List([]));
  };

  const handleAddSelectedGroup = (value: string): void => {
    const updatedGroups = selectedGroups.add(value);
    setIsAssigneeAdded(true);
    updateGroupAssignees(updatedGroups);
    setAssigneesToDisplay((previousAssignees) =>
      previousAssignees.concat(groupAssigneeAdapt(value))
    );
    setOpenSearch(false);
    setIsOpen(false);
  };

  const handleAddSelectedAssignee = (value: string): void => {
    const updatedAssignees = selectedAssignees.add(value);
    setIsAssigneeAdded(true);
    updateAssignees(updatedAssignees);

    setAssigneesToDisplay((previousAssignees) =>
      previousAssignees.concat(individualAssigneeAdapt(value))
    );
    setOpenSearch(false);
    setIsOpen(false);
  };

  const getGroupNameById = (id: string) => {
    const group = groupAssigneesOptions.find((group) => group.groupid === id);
    return group ? group.name : '';
  };

  const individualAssigneeAdapt = (assignee: string) => {
    const handleRemove =
      assignee === 'Multiple Assignees'
        ? handleRemoveAll
        : handleRemoveSelectedAssignee;
    return {
      key: assignee,
      id: `selected-assignee-${assignee}`,
      handleRemove,
      value: assignee,
    };
  };

  const groupAssigneeAdapt = (groupid: string) => {
    const value =
      groupid === 'Multiple Groups' ? groupid : getGroupNameById(groupid);
    return {
      key: groupid,
      id: `selected-group-${groupid}`,
      handleRemove: handleRemoveSelectedGroup,
      value,
    };
  };

  const mergeIndividualAndGroupAssignees = (
    individualAssignees: OrderedSet<string>,
    groupAssignees: OrderedSet<string>
  ) => {
    let individuals = List();
    let groups = List();
    if (individualAssignees.size > 0) {
      individuals = individualAssignees
        .map((assignee) => individualAssigneeAdapt(assignee))
        .toList();
    }

    if (groupAssignees.size > 0 && groupAssigneesOptions.size > 0) {
      groups = groupAssignees
        .map((groupid) => groupAssigneeAdapt(groupid))
        .toList();
    }

    const collator = new Intl.Collator(undefined, { numeric: true });
    return individuals
      .concat(groups)
      .sort((a, b) => collator.compare(a.value, b.value));
  };

  const renderAssignees = () => {
    return (
      <>
        {assigneesToDisplay.size > 0 &&
          assigneesToDisplay
            .filter((a) => a.value !== 'Multiple Groups')
            .map((assignee) => (
              <SelectedOptionLabel
                key={assignee.key}
                id={assignee.id}
                onLabelClick={(event: MouseEvent) => {
                  assignee.handleRemove(event, assignee.key);
                }}
                value={assignee.value}
              />
            ))}
        <div ref={assigneeInputRef} />
      </>
    );
  };

  const onSearchChange = (nextSearch: string) => {
    setOpenSearch(true);
    setHideSearchIcon(true);

    const filteredIndividuals = assigneesOptions.filter((option) =>
      option.toLowerCase().includes(nextSearch.toLowerCase())
    );

    const filteredGroups = groupAssigneesOptions.filter((option) =>
      option.name.toLowerCase().includes(nextSearch.toLowerCase())
    );

    setSearchIndividuals(filteredIndividuals);
    setSearchGroups(filteredGroups);

    if (nextSearch === '') {
      setOpenSearch(false);
      setHideSearchIcon(false);
    }
  };

  const renderPopover = () => {
    return (
      <TabsPopover open={isOpen}>
        <div className="px3 py3 w100">
          <SearchInput
            id="assignees-search"
            onChange={onSearchChange}
            placeholder={translate(
              'mapView:assigneesTabsMultiSelect.searchIndividualsAndTeams'
            )}
            hideSearchIcon={hideSearchIcon}
          />
        </div>
        {!openSearch ? (
          <>
            <Tabs selectedTab={selectedTab} onChange={handleTabChange}>
              <Tab
                name="Individuals"
                placeholder={translate(
                  'mapView:assigneesTabsMultiSelect.individuals'
                )}
              />
              <Tab
                name="Groups"
                placeholder={translate(
                  'mapView:assigneesTabsMultiSelect.teams'
                )}
              />
            </Tabs>
            <div className="oa" style={{ height: '176px' }}>
              {selectedTab === 'Individuals' ? (
                <AssigneesTab
                  handleAddSelected={handleAddSelectedAssignee}
                  options={assigneesOptions}
                  selectedOptions={selectedAssignees}
                />
              ) : (
                <GroupAssigneesTab
                  handleAddSelected={handleAddSelectedGroup}
                  options={groupAssigneesOptions}
                  selectedOptions={selectedGroups}
                />
              )}
            </div>
          </>
        ) : (
          <AssigneesSearchResult
            handleAddSelectedAssignee={handleAddSelectedAssignee}
            searchIndividuals={searchIndividuals}
            selectedAssignees={selectedAssignees}
            handleAddSelectedGroup={handleAddSelectedGroup}
            searchGroups={searchGroups}
            selectedGroups={selectedGroups}
          />
        )}
      </TabsPopover>
    );
  };

  const renderEditableAssigneesDropdown = () => {
    return (
      <>
        <OutsideClick onOutsideClick={() => setIsOpen(false)}>
          <div
            className={`boxMultiSelect ${isOpen ? 'open' : ''}`}
            onClick={() => setIsOpen(!isOpen)}
          >
            <div className="container">{renderAssignees()}</div>
            <div
              className="bgd-primary-blue b-radius-3 mr2 p2 pos-a cursor-pointer"
              style={{
                marginTop: '1.75rem',
                bottom: '0.5rem',
                right: '0rem',
                width: '40px',
                height: '40px',
                color: '#FFFFFF',
              }}
            >
              <Plus />
            </div>
          </div>
          {renderPopover()}
        </OutsideClick>
      </>
    );
  };

  const renderNonEditableAssignees = () => {
    const selectedGroupsNames = selectedGroups.map((group) =>
      getGroupNameById(group)
    );
    return (
      <div className="font-size-5 light">
        {[...selectedGroupsNames, ...selectedAssignees].join(', ')}
      </div>
    );
  };

  return (
    <div
      style={{
        position: 'relative',
      }}
    >
      {editable
        ? renderEditableAssigneesDropdown()
        : renderNonEditableAssignees()}
    </div>
  );
};

export default AssigneesTabsMultiSelect;
